// SPEC-021: Student Mini App content types. Kept standalone (not shared
// with the dashboard) so the two apps can evolve independently. Only the
// read-side shapes are defined — authoring lives in the dashboard.

export type ContentType = 'article' | 'audio' | 'video' | 'mixed';
export type ContentStatus =
  | 'draft'
  | 'pending_review'
  | 'published'
  | 'rejected'
  | 'archived';
export type ContentVisibility = 'public' | 'teacher_students' | 'tagged' | 'assigned';
export type ContentReactionType = 'like' | 'bookmark';
export type ContentSeedLinkType = 'prerequisite' | 'companion' | 'followup';
export type ContentEmbedProvider = 'youtube' | 'vimeo';
export type ContentEventType =
  | 'open'
  | 'heartbeat'
  | 'progress'
  | 'complete'
  | 'pause'
  | 'resume'
  | 'close'
  | 'react';

// ---------------------------------------------------------------------------
// Tiptap doc schema
// ---------------------------------------------------------------------------

export interface TiptapMark {
  type: string;
  attrs?: Record<string, unknown>;
}

export interface TiptapBlock {
  type: string;
  attrs?: Record<string, unknown>;
  content?: TiptapBlock[];
  text?: string;
  marks?: TiptapMark[];
}

export interface TiptapDoc {
  type: 'doc';
  content: TiptapBlock[];
}

// ---------------------------------------------------------------------------
// Core item
// ---------------------------------------------------------------------------

export interface ContentMediaAsset {
  id: string;
  content_id: string;
  asset_type: 'image' | 'audio' | 'file';
  s3_object_key: string;
  mime_type: string;
  size_bytes: number;
  width?: number | null;
  height?: number | null;
  duration_sec?: number | null;
  presigned_url?: string;
}

export interface ContentSeedLink {
  content_id: string;
  seed_id: string;
  link_type: ContentSeedLinkType;
  created_at: string;
}

export interface ContentTag {
  id: string;
  slug: string;
  name: string;
  color?: string | null;
  icon?: string | null;
}

export interface ContentItem {
  id: string;
  author_id: string;
  title: string;
  slug: string;
  summary?: string | null;
  cover_image_key?: string | null;
  content_type: ContentType;
  body: TiptapDoc;
  language: string;
  category?: string | null;
  estimated_minutes?: number | null;
  primary_audio_key?: string | null;
  primary_audio_duration_sec?: number | null;
  embed_provider?: ContentEmbedProvider | null;
  embed_url?: string | null;
  embed_video_id?: string | null;
  embed_duration_sec?: number | null;
  status: ContentStatus;
  visibility: ContentVisibility;
  min_subscription_tier: string;
  published_at?: string | null;
  created_at: string;
  updated_at: string;
  // Hydrated on the detail endpoint.
  tags?: ContentTag[];
  media?: ContentMediaAsset[];
  seed_links?: ContentSeedLink[];
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

export interface ContentEventPayload {
  event_type: ContentEventType;
  event_data?: Record<string, unknown>;
  occurred_at?: string;
}

export interface ContentEventBatch {
  device?: 'mini_app' | 'web';
  events: ContentEventPayload[];
}

// ---------------------------------------------------------------------------
// Query params
// ---------------------------------------------------------------------------

export interface ContentFeedParams {
  type?: ContentType;
  tag_id?: string;
  bookmarked?: boolean;
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

export const contentTypeLabels: Record<ContentType, string> = {
  article: 'Article',
  audio: 'Audio',
  video: 'Video',
  mixed: 'Mixed',
};

export const contentTypeEmojis: Record<ContentType, string> = {
  article: '📖',
  audio: '🎧',
  video: '🎬',
  mixed: '📚',
};
